import WatsonApi from "../backends/WatsonApi";
import {ReactiveContentItem} from "../components/schoolfeed/SchoolFeedContentItem";
import {IView} from "../stores/InterfaceStore";
import {unstable_serialize} from "swr/infinite";
import {getCacheKeyFromCurrentRangeUrlQuery} from "../components/schoolfeed/SchoolFeedEventsDisplay";
import {
    buildCacheKey,
    getSchoolFeedInfiniteScrollUniqueKey
} from "../components/schoolfeed/hooks/useSchoolFeedInfiniteScroll";
import {useSWRConfig} from "swr";
import {getQueries, isSchoolFeedStandalone} from "../utils/SchoolBlocksUtilities";
import {useStores} from "../stores/useStores";
import {useGridType} from "./useGridType";

export function useCreateContentItem() {
    const {interfaceStore, organizationStore, gridStore} = useStores();
    const { mutate } = useSWRConfig();
    const gridType = useGridType();

    async function handleSave (contentItem, organization, post_to_facebook: boolean) {
        const client = await WatsonApi();
        const result = await client.apis.organizations.organizations_content_items_create({
            "organization_pk": organization.id,
            "post_to_facebook": post_to_facebook,
            data: contentItem,
        })
        const item = new ReactiveContentItem({...JSON.parse(result.data), organization});
        if (item.is_news || interfaceStore.view === IView.SCHOOLFEED_PAGE || (interfaceStore.view === IView.DEFAULT && isSchoolFeedStandalone())) {
            let contentType: ISchoolFeedContentType | null = null;
            if (item.is_news) contentType = "news";
            else if (item.is_file || item.is_folder) contentType = "files";
            else if (item.is_media) contentType = "media";
            else if (item.is_event) contentType = "events";
            else if (item.is_coursework) contentType = "coursework";

            if (contentType) {
                const urlQuery = getQueries(interfaceStore.currentFullUrl);
                if (contentType === "events") {
                    if (urlQuery.currentRangeStart && urlQuery.currentRangeEnd) {
                        await mutate(unstable_serialize((index, previousPageData) => {
                            return getCacheKeyFromCurrentRangeUrlQuery(urlQuery);
                        }))
                    }
                } else {
                    async function addNewItem(item, contentType, urlQuery) {
                        const keyFn = (pageIndex, prev) =>
                            buildCacheKey(
                                pageIndex,
                                contentType,
                                { organization_id: item.organization_id },
                                { following: urlQuery.following }
                            );

                        const serializedKey = unstable_serialize(keyFn);

                        await mutate(
                            serializedKey,
                            (currentPages) => {
                                if (!currentPages) return currentPages;

                                const pages = [...currentPages];
                                pages[0] = {
                                    ...pages[0],
                                    contentItems: [new ReactiveContentItem(item), ...pages[0].contentItems],
                                    total: (pages[0].total ?? 0) + 1,
                                };

                                return pages;
                            },
                            {
                                revalidate: true,
                            }
                        );
                    }
                    await addNewItem(item, contentType, urlQuery);
                }
            }
        } else {
            // when creating an item from here, because of the complexity of shared_paths and
            // business logic of what items show up where, we can't really be sure what terrace
            // an item should show up on (if any), so it's safest to just revalidate everything
            const client = await WatsonApi();
            const result = await client.apis.organizations.organizations_read({
                organization_pk: organizationStore.currentOrganization.id,
                id: organizationStore.currentOrganization.id,
                expand: ["blocks"],
                gridType: gridType,
            });
            gridStore.setBlocks(JSON.parse(result.data).blocks);
        }
    }

    return { handleSave };
}
